import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useParams } from 'react-router-dom';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { toast } from 'react-toastify';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import ModalPrintSuccessControInteressato from './modal';

interface PrintComunicazioneControInteressatoProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
}

const AccordionPrintComunicazioneControInteressatoComponent = ({
  loadUpdateDate,
}: PrintComunicazioneControInteressatoProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const [openModal, setOpenModal] = useState(false);

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const params = useParams();

  const ref = useRef<HTMLDivElement | null>(null);

  const printComunication = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/stampa/comunicazione-a-contro-interessato`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const registerCommunication = useHttpClient(
    `${__PRACTICE_API_URL__}/comunicazioni/contro-interessato`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const handlePrint = useEventCallback(async () => {
    try {
      (
        printComunication({
          data: {
            codiceEnte: params?.codiceEnte,
            tipoRegistro: params?.tipoRegistro,
            idAnnoPratica: params?.idAnnoPratica
              ? Number.parseInt(params?.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params?.progressivoPratica
              ? Number.parseInt(params?.progressivoPratica, 10)
              : undefined,
          },
          responseType: 'blob',
        }) as ResponsePromise<Blob>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');
        const pdfCertificate = response?.data;
        if (pdfCertificate) {
          const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
          blobToBase64(blob).then((base64) => {
            setPdfModal({
              data: base64.split(',')[1],
              name: 'Stampa Comunicazione a Controinteressato',
              open: true,
            });
          });
        }
      });
    } catch (error) {
      toast.error('Errore durante la generazione del documento');
    }
  });

  const onYesModalPrint = useEventCallback(async () => {
    try {
      (
        registerCommunication({
          data: {
            codiceEnte: params?.codiceEnte,
            tipoRegistro: params?.tipoRegistro,
            idAnnoPratica: params?.idAnnoPratica
              ? Number.parseInt(params?.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params?.progressivoPratica
              ? Number.parseInt(params?.progressivoPratica, 10)
              : undefined,
          },
        }) as ResponsePromise<void>
      ).then(async () => {
        toast.success('Operazione eseguita con successo');
        setOpenModal(false);
        await loadUpdateDate();
      });
    } catch (error) {
      toast.error('Errore durante la registrazione della comunicazione');
    }
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-print-contro-interessato-content"
          id="panel-print-contro-interessato-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Stampa Comunicazione a Controinteressato</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Genera e scarica la comunicazione di avvio procedimento da inviare al proprietario
            dell&apos;immobile (controinteressato).
          </Typography>
          <div className="pmi-align-right pmi-pt-2">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={handlePrint}
              id="print-contro-interessato"
            >
              Stampa
            </PmiButton>
          </div>
        </AccordionDetails>
      </Accordion>
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Stampa Comunicazione a Controinteressato"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
          setOpenModal(true);
        }}
      />
      <ModalPrintSuccessControInteressato
        open={openModal}
        onClose={() => setOpenModal(false)}
        onYes={onYesModalPrint}
      />
    </Fragment>
  );
};

AccordionPrintComunicazioneControInteressatoComponent.displayName =
  'Accordion Print Comunicazione Contro Interessato component';

const AccordionPrintComunicazioneControInteressato = memo(
  AccordionPrintComunicazioneControInteressatoComponent
);

export default AccordionPrintComunicazioneControInteressato;
